/**
 * Health Records Firestore Service
 * Collections under babies/{babyId}/vaccines, visits, medications, medicineLogs
 * Mirrors patterns from firestore-baby.ts
 */

import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  getDoc,
  query,
  orderBy,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import type { VaccineRecord, Visit, Medication, MedicineDoseLog, VaccineSchedulePreference, BookletScheduleEntry } from '../types';

function getHealthCollections(babyId: string) {
  return {
    vaccines:     collection(db, 'babies', babyId, 'vaccines'),
    visits:       collection(db, 'babies', babyId, 'visits'),
    medications:  collection(db, 'babies', babyId, 'medications'),
    medicineLogs: collection(db, 'babies', babyId, 'medicineLogs'),
  };
}

// ─── Vaccines ────────────────────────────────────────────────────────────────

export async function addVaccineRecord(
  babyId: string,
  record: Omit<VaccineRecord, 'id'>
): Promise<VaccineRecord> {
  const col = getHealthCollections(babyId).vaccines;
  const ref = await addDoc(col, { ...record, createdAt: serverTimestamp() });
  return { id: ref.id, ...record };
}

// Batch add multiple vaccine records in a single Firestore commit (one round-trip)
export async function batchAddVaccineRecords(
  babyId: string,
  records: Omit<VaccineRecord, 'id'>[]
): Promise<void> {
  const col = collection(db, 'babies', babyId, 'vaccines');
  const batch = writeBatch(db);
  for (const record of records) {
    const ref = doc(col);
    batch.set(ref, { ...record, createdAt: serverTimestamp() });
  }
  await batch.commit();
}

export async function getVaccineRecords(babyId: string): Promise<VaccineRecord[]> {
  const col = getHealthCollections(babyId).vaccines;
  const snap = await getDocs(query(col, orderBy('dateGiven', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as VaccineRecord));
}

export async function deleteVaccineRecord(babyId: string, vaccineId: string): Promise<void> {
  const col = getHealthCollections(babyId).vaccines;
  await deleteDoc(doc(col, vaccineId));
}

export async function updateVaccineRecord(
  babyId: string,
  vaccineId: string,
  patch: Partial<Omit<VaccineRecord, 'id'>>
): Promise<void> {
  const col = getHealthCollections(babyId).vaccines;
  await updateDoc(doc(col, vaccineId), patch);
}

// ─── Vaccine Schedule Preference ─────────────────────────────────────────────

export async function getVaccinePreference(babyId: string): Promise<VaccineSchedulePreference> {
  const ref = doc(db, 'babies', babyId, 'vaccinePrefs', 'schedule');
  const snap = await getDoc(ref);
  if (!snap.exists()) return { source: 'govt' };

  const data = snap.data() as VaccineSchedulePreference & {
    // backwards compat: old field name
    bookletVaccines?: { name: string; scheduleId?: string; dueOffsetDays: number }[];
  };

  // Migrate: if bookletSchedule absent but old bookletVaccines present, surface the old data
  // so callers can still read it (VaccineSchedulePreference keeps bookletVaccines for read-compat)
  return data;
}

/** Strip undefined from BookletScheduleEntry objects — Firestore rejects undefined in nested arrays. */
function sanitizeBookletSchedule(entries: BookletScheduleEntry[]): Record<string, unknown>[] {
  return entries.map(e => {
    const out: Record<string, unknown> = { index: e.index, name: e.name, isGiven: e.isGiven };
    if (e.givenDate !== undefined) out.givenDate = e.givenDate;
    if (e.dueLabel !== undefined) out.dueLabel = e.dueLabel;
    if (e.brand !== undefined) out.brand = e.brand;
    if (e.batchNumber !== undefined) out.batchNumber = e.batchNumber;
    return out;
  });
}

export async function saveVaccinePreference(
  babyId: string,
  pref: VaccineSchedulePreference
): Promise<void> {
  const ref = doc(db, 'babies', babyId, 'vaccinePrefs', 'schedule');
  // Never write undefined values — strip them before persisting
  const data: Record<string, unknown> = { source: pref.source };
  if (pref.bookletSchedule !== undefined) {
    data.bookletSchedule = sanitizeBookletSchedule(pref.bookletSchedule);
  }
  // Do NOT write bookletVaccines — it is read-only for backwards compat
  await setDoc(ref, data);
}

/**
 * Merge an updated booklet schedule into Firestore.
 * Called during re-scan (update mode). Uses `index` as the primary merge key:
 *   - Existing entries at the same index: updated in place (date, brand, etc.)
 *   - New entries (index beyond current length): appended
 * Caller is responsible for constructing the merged array before calling this.
 */
export async function updateBookletSchedule(
  babyId: string,
  mergedSchedule: BookletScheduleEntry[]
): Promise<void> {
  const ref = doc(db, 'babies', babyId, 'vaccinePrefs', 'schedule');
  await setDoc(ref, { source: 'booklet', bookletSchedule: sanitizeBookletSchedule(mergedSchedule) }, { merge: true });
}

// ─── Visits ──────────────────────────────────────────────────────────────────

export async function addVisit(
  babyId: string,
  visit: Omit<Visit, 'id'>
): Promise<Visit> {
  const col = getHealthCollections(babyId).visits;
  const ref = await addDoc(col, { ...visit, createdAt: serverTimestamp() });
  return { id: ref.id, ...visit };
}

export async function getVisits(babyId: string): Promise<Visit[]> {
  const col = getHealthCollections(babyId).visits;
  const snap = await getDocs(query(col, orderBy('date', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Visit));
}

export async function updateVisit(
  babyId: string,
  visitId: string,
  updates: Partial<Omit<Visit, 'id'>>
): Promise<void> {
  const col = getHealthCollections(babyId).visits;
  await updateDoc(doc(col, visitId), updates);
}

export async function deleteVisit(babyId: string, visitId: string): Promise<void> {
  const col = getHealthCollections(babyId).visits;
  await deleteDoc(doc(col, visitId));
}

// ─── Medications ─────────────────────────────────────────────────────────────

export async function addMedication(
  babyId: string,
  med: Omit<Medication, 'id'>
): Promise<Medication> {
  const col = getHealthCollections(babyId).medications;
  const ref = await addDoc(col, { ...med, createdAt: serverTimestamp() });
  return { id: ref.id, ...med };
}

export async function getMedications(babyId: string): Promise<Medication[]> {
  const col = getHealthCollections(babyId).medications;
  const snap = await getDocs(query(col, orderBy('startDate', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Medication));
}

export async function updateMedication(
  babyId: string,
  medicationId: string,
  updates: Partial<Omit<Medication, 'id'>>
): Promise<void> {
  const col = getHealthCollections(babyId).medications;
  await updateDoc(doc(col, medicationId), updates);
}

export async function deleteMedication(babyId: string, medicationId: string): Promise<void> {
  const col = getHealthCollections(babyId).medications;
  await deleteDoc(doc(col, medicationId));
}

// ─── Medicine Logs (daily tick-off) ──────────────────────────────────────────

/** Format a Date as YYYY-MM-DD for use as a document ID */
function dateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Log a single dose as done for today */
export async function logMedicineDose(
  babyId: string,
  medicationId: string,
  timing: string,
  date: Date = new Date()
): Promise<void> {
  const col = getHealthCollections(babyId).medicineLogs;
  const docRef = doc(col, dateKey(date));
  // Merge so we don't overwrite other medications' logs for the same day
  await setDoc(
    docRef,
    {
      [`${medicationId}_${timing}`]: {
        done: true,
        timestamp: new Date().toISOString(),
      } satisfies MedicineDoseLog,
    },
    { merge: true }
  );
}

/** Undo a dose log */
export async function unlogMedicineDose(
  babyId: string,
  medicationId: string,
  timing: string,
  date: Date = new Date()
): Promise<void> {
  const col = getHealthCollections(babyId).medicineLogs;
  const docRef = doc(col, dateKey(date));
  await setDoc(
    docRef,
    {
      [`${medicationId}_${timing}`]: {
        done: false,
        timestamp: new Date().toISOString(),
      } satisfies MedicineDoseLog,
    },
    { merge: true }
  );
}

/** Get the full medicine log for a given date (returns map of `medicationId_timing` → DoseLog) */
export async function getMedicineLog(
  babyId: string,
  date: Date = new Date()
): Promise<Record<string, MedicineDoseLog>> {
  const col = getHealthCollections(babyId).medicineLogs;
  const docRef = doc(col, dateKey(date));
  const snap = await getDoc(docRef);
  if (!snap.exists()) return {};
  return snap.data() as Record<string, MedicineDoseLog>;
}
